import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, SideSheet, Skeleton, Table, Tag, Typography } from "@douyinfe/semi-ui";
import dayjs from "dayjs";
import { AiCoachApi, BreakdownItem, intentionLevelLabel, intentionLevelStyle } from "../../../api/aiCoach";
import { displayLeadStatus } from "../../../api/ApiClient";
import { tagColor } from "../constants";
import { TextCell, TextCellRemark } from "../../../comp/cell";

const { Text } = Typography;

interface Props {
  visible: boolean;
  title: string;
  reportId: number | null;
  /** metric = 指标明细；resistance = 抗拒点明细 */
  mode: "metric" | "resistance";
  /** metric 模式下是 metricKey；resistance 模式下是 term 文本 */
  payload: string;
  onClose: () => void;
}

const openLeadDetail = (id: number) => {
  const w = window.open("about:blank");
  if (w) {
    const data = { id, ids: [] };
    w.location.href = "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
  }
};

const openLeadProfile = (id: number) => {
  window.open(`/#/leadProfile?id=${id}`);
};

const BreakdownDrawer: React.FC<Props> = ({ visible, title, reportId, mode, payload, onClose }) => {
  const [rows, setRows] = useState<BreakdownItem[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!reportId || !payload) return;
    setLoading(true);
    try {
      const resp = mode === "metric"
        ? await AiCoachApi.metricBreakdown(reportId, payload)
        : await AiCoachApi.resistanceBreakdown(reportId, payload);
      setRows(resp.data ?? []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [reportId, mode, payload]);

  useEffect(() => {
    if (visible) reload();
  }, [visible, reload]);

  const columns = [
    {
      title: "客户",
      dataIndex: "id",
      width: 100,
      render: (id: number, r: BreakdownItem) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Text link strong onClick={() => openLeadDetail(id)}>
            {r.name || `#${id}`}
          </Text>
          <Text type="tertiary" size="small">ID: {id}</Text>
        </div>
      ),
    },
    {
      title: "意向",
      dataIndex: "intentionLevel",
      width: 100,
      render: (lvl?: string) => lvl ? (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={intentionLevelStyle(lvl)}>{lvl}</span>
          <Text type="tertiary" size="small">{intentionLevelLabel(lvl)}</Text>
        </div>
      ) : <Text type="tertiary">-</Text>,
    },
    {
      title: "星级",
      dataIndex: "star",
      width: 70,
      render: (v?: number) => v != null ? `${v}★` : <Text type="tertiary">-</Text>,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (s?: string) => s ? <Tag color="blue">{displayLeadStatus(s)}</Tag> :
        <Text type="tertiary">-</Text>,
    },
    {
      title: "资质",
      dataIndex: "remark",
      width: 200,
      render: (a, b, c) => <TextCell text={b.remark} width={200} />,
    },
    {
      title: "本周跟进",
      dataIndex: "weekCallCount",
      width: 80,
      render: (v: number) => <Text strong>{v ?? 0}</Text>,
    },
    {
      title: "最近跟进时间",
      dataIndex: "lastContactAt",
      width: 140,
      render: (v?: string) => v ? dayjs(v).format("MM-DD HH:mm") : <Text type="tertiary">-</Text>,
    },
    {
      title: "最近跟进记录",
      dataIndex: "contactLogs",
      width: 240,
      render: (value, index) => (
              <TextCellRemark moreText={value ? value : []} width={200} />
      ),
    },
    {
      title: "抗拒点",
      dataIndex: "resistancePoints",
      render: (arr?: string[]) => (arr && arr.length) ?
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {arr.map((t, i) => <Tag key={i} size="small" color={tagColor(t)}>{t}</Tag>)}
        </div> : <Text type="tertiary">-</Text>,
    },
    {
      title: "操作",
      width: 140,
      fixed: "right" as const,
      render: (_: unknown, r: BreakdownItem) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Button size="small" theme="borderless" onClick={() => openLeadProfile(r.id)}>画像</Button>
        </div>
      ),
    },
  ];

  return (
    <SideSheet
      visible={visible}
      onCancel={onClose}
      title={title}
      width={1100}
      placement="right"
      bodyStyle={{ padding: 5 }}
    >
      {loading ? (
        <div style={{ padding: 16 }}>
          <Skeleton.Paragraph rows={6} />
        </div>
      ) : rows.length === 0 ? (
        <Empty description="无匹配客户" style={{ padding: 60 }} />
      ) : (
        <Table<BreakdownItem>
          columns={columns as any}
          dataSource={rows}
          rowKey="id"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1200 }}
          size="small"
        />
      )}
    </SideSheet>
  );
};

export default BreakdownDrawer;
